// ── Leaderboard Panel ── Toggleable overlay showing top players by rating
// Toggle with L key during gameplay. Fetches from /api/leaderboard endpoint.
// Supports pagination, page size selection, and column sorting.

import { createRankBadge, getRankTier, type RankTier } from './components/rank-badge';
import {
  showLeaderboard as showCompactLeaderboard,
  hideLeaderboard as hideCompactLeaderboard,
  type LeaderboardEntry as CompactLeaderboardEntry,
} from './components/leaderboard';

const ACCENT = '#e94560';
const GOLD = '#e8b849';
const SILVER = '#c0c0c0';
const BRONZE = '#cd7f32';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';

// ── Pagination & sorting types ──

export type SortColumn = 'rating' | 'kd' | 'wins';
export type SortDirection = 'asc' | 'desc';

export const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
export const DEFAULT_PAGE_SIZE = 50;

export interface PaginateResult<T> {
  items: T[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

/** Paginate an array client-side. */
export function paginate<T>(items: T[], page: number, pageSize: number): PaginateResult<T> {
  const safePage = Math.max(1, page);
  const totalItems = items.length;
  const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / pageSize);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageItems = start < totalItems ? items.slice(start, end) : [];
  return { items: pageItems, currentPage: safePage, totalPages, totalItems };
}

/** Sort players by column. Returns a new sorted array. */
export function sortPlayers(
  players: LeaderboardPlayerEntry[],
  column: SortColumn,
  direction: SortDirection,
): LeaderboardPlayerEntry[] {
  const sorted = [...players];
  const mult = direction === 'asc' ? 1 : -1;
  sorted.sort((a, b) => {
    switch (column) {
      case 'rating': return (a.rating - b.rating) * mult;
      case 'kd': return (a.kd_ratio - b.kd_ratio) * mult;
      case 'wins': return (a.wins - b.wins) * mult;
      default: return 0;
    }
  });
  return sorted;
}

/** Build the fetch URL from serverUrl, limit, and offset. */
export function buildFetchUrl(serverUrl: string, limit: number, offset: number): string {
  return serverUrl
    .replace('ws://', 'http://')
    .replace('wss://', 'https://')
    .replace('/ws', '') + `/api/leaderboard?limit=${limit}&offset=${offset}`;
}

export interface LeaderboardPlayerEntry {
  rank: number;
  display_name: string;
  username?: string | null;
  wallet_address?: string | null;
  rating: number;
  rank_tier: number;
  tier_name: string;
  wins: number;
  losses: number;
  total_kills: number;
  total_deaths: number;
  kd_ratio: number;
}

export class LeaderboardPanel {
  private overlay: HTMLElement;
  private tableBody: HTMLElement;
  private loadingEl: HTMLElement;
  private visible = false;
  private serverUrl: string;
  private boundKeyHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
  private abortController: AbortController | null = null;

  // Pagination state
  private currentPage = 1;
  private pageSize: number = DEFAULT_PAGE_SIZE;
  private totalCount = 0;
  private allPlayers: LeaderboardPlayerEntry[] = [];

  // Sort state
  private sortColumn: SortColumn = 'rating';
  private sortDirection: SortDirection = 'desc';

  // Pagination UI elements
  private paginationContainer!: HTMLElement;
  private prevBtn!: HTMLElement;
  private nextBtn!: HTMLElement;
  private pageIndicator!: HTMLElement;
  private pageSizeSelect!: HTMLSelectElement;

  // Column header elements
  private colHeaderContainer!: HTMLElement;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
    const { overlay, tableBody, loadingEl } = this.createOverlay();
    this.overlay = overlay;
    this.tableBody = tableBody;
    this.loadingEl = loadingEl;
    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  async show(): Promise<void> {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.currentPage = 1;
    await this.fetchAndRender();
  }

  private async fetchAndRender(): Promise<void> {
    this.loadingEl.style.display = 'block';
    this.tableBody.innerHTML = '';

    // Abort any in-flight fetch before starting a new one
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const offset = (this.currentPage - 1) * this.pageSize;
      const httpUrl = buildFetchUrl(this.serverUrl, this.pageSize, offset);

      const res = await fetch(httpUrl, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.totalCount = data.total ?? data.players?.length ?? 0;
      this.allPlayers = data.players ?? [];
      this.renderCurrentView();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('[Leaderboard] Fetch failed:', err);
      this.tableBody.innerHTML =
        '<div style="color:#666;padding:20px;text-align:center;">Failed to load leaderboard</div>';
    } finally {
      this.loadingEl.style.display = 'none';
    }
  }

  private renderCurrentView(): void {
    // Sort current page data client-side
    const sorted = sortPlayers(this.allPlayers, this.sortColumn, this.sortDirection);
    this.renderPlayers(sorted);
    this.updatePagination();
    this.updateColumnHeaders();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    hideCompactLeaderboard();
  }

  /** Switch to the compact `components/leaderboard` layout with the current rows. */
  private showCompactView(): void {
    const compactEntries: CompactLeaderboardEntry[] = this.allPlayers.map((p) => ({
      rank: p.rank,
      name: p.username ?? p.display_name,
      rating: Math.round(p.rating),
      tier: (p.tier_name as RankTier | undefined) ?? getRankTier(p.rating),
      wins: p.wins,
      losses: p.losses,
      winRate: p.wins + p.losses === 0 ? 0 : Math.round((p.wins / (p.wins + p.losses)) * 100),
    }));
    this.overlay.style.display = 'none';
    showCompactLeaderboard(compactEntries);
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.overlay.remove();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
    }
  }

  private renderPlayers(players: LeaderboardPlayerEntry[]): void {
    this.tableBody.innerHTML = '';

    if (players.length === 0) {
      this.tableBody.innerHTML =
        '<div style="color:#666;padding:20px;text-align:center;">No players ranked yet</div>';
      return;
    }

    for (const p of players) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: '36px minmax(80px, 1fr) 60px 50px 60px 50px',
        padding: '10px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'center',
        fontSize: '13px',
        transition: 'background 0.1s',
      });
      row.addEventListener('mouseenter', () => {
        row.style.background = 'rgba(255,255,255,0.03)';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = 'transparent';
      });

      // Rank
      const rankEl = document.createElement('div');
      rankEl.style.fontWeight = 'bold';
      rankEl.style.fontSize = '15px';
      if (p.rank === 1) rankEl.style.color = GOLD;
      else if (p.rank === 2) rankEl.style.color = SILVER;
      else if (p.rank === 3) rankEl.style.color = BRONZE;
      else rankEl.style.color = '#888';
      rankEl.textContent = `#${p.rank}`;
      row.appendChild(rankEl);

      // Name + wallet (truncated)
      const nameCell = document.createElement('div');
      const displayName = p.username ?? p.display_name;
      const nameSpan = document.createElement('span');
      nameSpan.style.color = '#fff';
      nameSpan.style.fontWeight = 'bold';
      nameSpan.textContent = displayName;
      nameCell.appendChild(nameSpan);

      if (p.wallet_address) {
        const walletSpan = document.createElement('span');
        walletSpan.style.color = '#555';
        walletSpan.style.fontSize = '10px';
        walletSpan.style.marginLeft = '8px';
        const addr = p.wallet_address;
        walletSpan.textContent = addr.slice(0, 4) + '...' + addr.slice(-4);
        nameCell.appendChild(walletSpan);
      }

      // Tier badge — use the rich RankBadge component for the top 10 slots,
      // and fall back to a plain tier label for deeper pages so the DOM stays
      // cheap for long leaderboards.
      if (p.rank <= 10) {
        const tier = (p.tier_name as RankTier | undefined) ?? getRankTier(p.rating);
        const badge = createRankBadge(Math.round(p.rating), tier);
        Object.assign(badge.style, {
          marginTop: '4px',
          padding: '3px 8px',
          fontSize: '10px',
        });
        nameCell.appendChild(badge);
      } else {
        const tierSpan = document.createElement('div');
        tierSpan.style.fontSize = '10px';
        tierSpan.style.color = '#666';
        tierSpan.style.marginTop = '2px';
        tierSpan.textContent = p.tier_name ?? '';
        nameCell.appendChild(tierSpan);
      }
      row.appendChild(nameCell);

      // Rating
      const ratingEl = document.createElement('div');
      ratingEl.style.color = ACCENT;
      ratingEl.style.fontWeight = 'bold';
      ratingEl.style.textAlign = 'center';
      ratingEl.textContent = Math.round(p.rating).toString();
      row.appendChild(ratingEl);

      // K/D
      const kdEl = document.createElement('div');
      kdEl.style.textAlign = 'center';
      kdEl.style.color = p.kd_ratio >= 1.0 ? '#4caf50' : '#ff5252';
      kdEl.textContent = p.kd_ratio.toFixed(2);
      row.appendChild(kdEl);

      // Kills / Deaths
      const killsEl = document.createElement('div');
      killsEl.style.textAlign = 'center';
      killsEl.style.color = '#aaa';
      killsEl.textContent = `${p.total_kills}/${p.total_deaths}`;
      row.appendChild(killsEl);

      // Wins
      const winsEl = document.createElement('div');
      winsEl.style.textAlign = 'center';
      winsEl.style.color = '#aaa';
      winsEl.textContent = `${p.wins}W`;
      row.appendChild(winsEl);

      this.tableBody.appendChild(row);
    }
  }

  private updatePagination(): void {
    const totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));

    // Update page indicator
    if (this.totalCount > 0) {
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.totalCount);
      this.pageIndicator.textContent = `${start}-${end} of ${this.totalCount}`;
    } else {
      this.pageIndicator.textContent = 'No players ranked yet';
    }

    // Prev button state
    if (this.currentPage <= 1) {
      this.prevBtn.style.opacity = '0.3';
      this.prevBtn.style.pointerEvents = 'none';
    } else {
      this.prevBtn.style.opacity = '1';
      this.prevBtn.style.pointerEvents = 'auto';
    }

    // Next button state
    if (this.currentPage >= totalPages) {
      this.nextBtn.style.opacity = '0.3';
      this.nextBtn.style.pointerEvents = 'none';
    } else {
      this.nextBtn.style.opacity = '1';
      this.nextBtn.style.pointerEvents = 'auto';
    }
  }

  private updateColumnHeaders(): void {
    const sortableHeaders = this.colHeaderContainer.querySelectorAll<HTMLElement>('[data-sort-key]');
    for (const header of sortableHeaders) {
      const key = header.getAttribute('data-sort-key');
      const baseLabel = key === 'rating' ? 'RATING' : key === 'kd' ? 'K/D' : 'WINS';
      header.setAttribute('aria-sort', key === this.sortColumn ? (this.sortDirection === 'desc' ? 'descending' : 'ascending') : 'none');
      if (key === this.sortColumn) {
        const arrow = this.sortDirection === 'desc' ? ' \u25BC' : ' \u25B2';
        header.textContent = baseLabel + arrow;
        header.style.color = ACCENT;
      } else {
        header.textContent = baseLabel;
        header.style.color = '#666';
      }
    }
  }

  private createOverlay(): {
    overlay: HTMLElement;
    tableBody: HTMLElement;
    loadingEl: HTMLElement;
  } {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '57',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    // Card container
    const card = document.createElement('div');
    Object.assign(card.style, {
      width: 'min(700px, calc(100vw - 24px))',
      maxHeight: '80vh',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });

    // Header
    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '24px 24px 16px',
      borderBottom: `1px solid ${ACCENT}30`,
      textAlign: 'center',
    });

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '24px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: ACCENT,
      marginBottom: '4px',
    });
    title.textContent = 'LEADERBOARD';
    header.appendChild(title);

    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: '11px',
      color: '#555',
      letterSpacing: '2px',
    });
    subtitle.textContent = 'TOP PLAYERS BY RATING';
    header.appendChild(subtitle);
    card.appendChild(header);

    // Column headers (sortable)
    const colHeaders = document.createElement('div');
    Object.assign(colHeaders.style, {
      display: 'grid',
      gridTemplateColumns: '36px minmax(80px, 1fr) 60px 50px 60px 50px',
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    this.colHeaderContainer = colHeaders;

    const colDefs: Array<{ label: string; sortKey?: SortColumn }> = [
      { label: '#' },
      { label: 'PLAYER' },
      { label: 'RATING', sortKey: 'rating' },
      { label: 'K/D', sortKey: 'kd' },
      { label: 'K / D' },
      { label: 'WINS', sortKey: 'wins' },
    ];

    for (let i = 0; i < colDefs.length; i++) {
      const def = colDefs[i];
      const col = document.createElement('div');
      if (i >= 2) col.style.textAlign = 'center';

      if (def.sortKey) {
        col.style.cursor = 'pointer';
        col.style.transition = 'color 0.15s';
        col.setAttribute('data-sort-key', def.sortKey);
        col.textContent = def.label;
        col.addEventListener('mouseenter', () => { col.style.color = '#aaa'; });
        col.addEventListener('mouseleave', () => {
          col.style.color = col.getAttribute('data-sort-key') === this.sortColumn ? ACCENT : '#666';
        });
        col.addEventListener('click', () => {
          const key = def.sortKey!;
          if (this.sortColumn === key) {
            this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
          } else {
            this.sortColumn = key;
            this.sortDirection = 'desc';
          }
          this.renderCurrentView();
        });
      } else {
        col.textContent = def.label;
      }
      colHeaders.appendChild(col);
    }
    card.appendChild(colHeaders);

    // Scrollable body
    const tableBody = document.createElement('div');
    Object.assign(tableBody.style, {
      flex: '1',
      overflowY: 'auto',
      minHeight: '200px',
    });
    card.appendChild(tableBody);

    // Loading indicator
    const loadingEl = document.createElement('div');
    Object.assign(loadingEl.style, {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '14px',
      letterSpacing: '2px',
      display: 'none',
    });
    loadingEl.textContent = 'LOADING...';
    tableBody.appendChild(loadingEl);

    // Pagination controls
    this.paginationContainer = document.createElement('div');
    Object.assign(this.paginationContainer.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 16px',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      fontSize: '12px',
    });

    // Page size selector
    const pageSizeGroup = document.createElement('div');
    Object.assign(pageSizeGroup.style, { display: 'flex', alignItems: 'center', gap: '6px' });
    const pageSizeLabel = document.createElement('span');
    pageSizeLabel.style.color = '#666';
    pageSizeLabel.style.fontSize = '10px';
    pageSizeLabel.textContent = 'Per page:';
    pageSizeGroup.appendChild(pageSizeLabel);

    this.pageSizeSelect = document.createElement('select');
    Object.assign(this.pageSizeSelect.style, {
      background: 'rgba(40,40,30,0.9)',
      color: '#c8c090',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '3px',
      padding: '3px 6px',
      fontSize: '11px',
      fontFamily: FONT,
      cursor: 'pointer',
      outline: 'none',
    });
    for (const size of PAGE_SIZE_OPTIONS) {
      const opt = document.createElement('option');
      opt.value = String(size);
      opt.textContent = String(size);
      if (size === this.pageSize) opt.selected = true;
      this.pageSizeSelect.appendChild(opt);
    }
    this.pageSizeSelect.addEventListener('change', () => {
      this.pageSize = parseInt(this.pageSizeSelect.value, 10);
      this.currentPage = 1;
      this.fetchAndRender();
    });
    pageSizeGroup.appendChild(this.pageSizeSelect);
    this.paginationContainer.appendChild(pageSizeGroup);

    // Page navigation
    const navGroup = document.createElement('div');
    Object.assign(navGroup.style, { display: 'flex', alignItems: 'center', gap: '10px' });

    this.prevBtn = document.createElement('div');
    Object.assign(this.prevBtn.style, {
      padding: '4px 12px',
      fontSize: '11px',
      background: 'rgba(60,60,50,0.4)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '3px',
      cursor: 'pointer',
      color: '#888',
      transition: 'all 0.15s',
    });
    this.prevBtn.textContent = 'PREV';
    this.prevBtn.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchAndRender();
      }
    });
    navGroup.appendChild(this.prevBtn);

    this.pageIndicator = document.createElement('span');
    Object.assign(this.pageIndicator.style, { color: '#888', fontSize: '11px', letterSpacing: '1px' });
    this.pageIndicator.textContent = 'Page 1';
    navGroup.appendChild(this.pageIndicator);

    this.nextBtn = document.createElement('div');
    Object.assign(this.nextBtn.style, {
      padding: '4px 12px',
      fontSize: '11px',
      background: 'rgba(60,60,50,0.4)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '3px',
      cursor: 'pointer',
      color: '#888',
      transition: 'all 0.15s',
    });
    this.nextBtn.textContent = 'NEXT';
    this.nextBtn.addEventListener('click', () => {
      const totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
      if (this.currentPage < totalPages) {
        this.currentPage++;
        this.fetchAndRender();
      }
    });
    navGroup.appendChild(this.nextBtn);

    // Compact view toggle — renders the current rows through the DOM-based
    // `components/leaderboard` layout (search + pagination + myRank callout).
    const compactBtn = document.createElement('div');
    Object.assign(compactBtn.style, {
      padding: '4px 12px',
      fontSize: '11px',
      background: 'rgba(60,60,50,0.4)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '3px',
      cursor: 'pointer',
      color: '#888',
      transition: 'all 0.15s',
      marginLeft: '12px',
    });
    compactBtn.textContent = 'COMPACT';
    compactBtn.addEventListener('click', () => this.showCompactView());
    navGroup.appendChild(compactBtn);

    this.paginationContainer.appendChild(navGroup);
    card.appendChild(this.paginationContainer);

    // Footer hint
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '12px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = '[L] Toggle Leaderboard  |  [ESC] Close';
    card.appendChild(footer);

    overlay.appendChild(card);

    // Click outside to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hide();
    });

    return { overlay, tableBody, loadingEl };
  }
}
